import { ApiBase } from "@/api/ApiBase";
import { Formatter } from "@/utils/Formatter";

export type TipoConta = "Corrente" | "Investimento" | "Poupanca" | "Outros";
export type NivelConta = "Principal" | "Alternativo";
export type TipoMovimento = "CREDITO" | "DEBITO";
export type OperacaoMovimento = "MANUAL" | "AUTOMATICA";

export interface Banco {
  id: number;
  nome: string | null;
}

export interface CriadorResumo {
  id: number;
  nome: string;
}

export interface Conta {
  id: number;
  nome: string;
  tipo: TipoConta;
  saldo: number;
  totalReceitas: number;
  totalDespesas: number;
}

export interface ContaDetalhe {
  id: number;
  nome: string;
  nivel: NivelConta;
  tipo: TipoConta;
  iban: string | null;
  numeroConta: string | null;
  banco: Banco | null;
  criador: CriadorResumo;
  dataCriacao: string;
}

export interface InfoAllContasResponse {
  saldoGeral: number;
  saldoCreditoEsteMes: number;
  saldoDebitoEsteMes: number;
  bancos: Banco[];
  contas: Conta[];
}

export interface InfoOneContaResponse {
  conta: ContaDetalhe;
  valorTotalTransferenciasEnviadas: number;
  valorTotalTransferenciasRecebidas: number;
  valorTotalDespesas: number;
  valorTotalReceitas: number;
  bancos: Banco[];
  contas: Conta[];
}

export interface TesourariaResponse {
  msg: string;
  tipo: "sucesso" | "erro";
}

export interface ResultadoCategoriaTesouraria {
  nome: string;
  tipo: "sucesso" | "erro";
  msg: string;
}

export interface AddCategoriasTesourariaResponse {
  msg: string;
  tipo: "sucesso" | "erro" | "parcial";
  resultados: ResultadoCategoriaTesouraria[];
}

export const LIMITE_CATEGORIAS_TESOURARIA_POR_REQUISICAO = 20;

export interface NovaConta {
  nome: string;
  tipo: TipoConta;
  bancoId: number;
  iban: string;
  numeroConta: string;
}

export interface NovoMovimento {
  contaId: number;
  categoriaId: number;
  valor: number;
  data: string;
  descricao: string;
  comprovativo: File | null;
}

export interface NovaTransferencia {
  origemId: number;
  destinoId: number;
  categoriaId: number;
  valor: number;
  descricao: string;
  comprovativo: File | null;
}

export interface Movimento {
  id: number;
  dataCriacao: string;
  data: string;
  valor: number;
  descricao: string;
  comprovativo: string | null;
  tipo: TipoMovimento;
  operacao: OperacaoMovimento;
  nomeCriador: string;
  grupoCriador: string;
  nomeConta?: string;
  tipoConta?: string;
}

export interface Transferencia {
  id: number;
  dataCriacao: string;
  montante: number;
  descricao: string;
  comprovativo: string | null;
  nomeCriador: string;
  grupoCriador: string;
  nomeContaOrigem: string;
  tipoContaOrigem: string;
  nomeContaDestino: string;
  tipoContaDestino: string;
}

export interface CategoriaTesouraria {
  id: number;
  nome: string;
  totalMovimentos: number;
}

export class TesourariaApi extends ApiBase {
  infoAllContas(): Promise<InfoAllContasResponse> {
    return this.http.put<InfoAllContasResponse>(
      "/api/tesouraria/conta/info-all-contas",
    );
  }

  infoOneConta(id: number): Promise<InfoOneContaResponse> {
    return this.http.put<InfoOneContaResponse>(
      `/api/tesouraria/conta/info-one-contas/${id}`,
    );
  }

  listaMovimentos(pagina: number): Promise<Movimento[]> {
    return this.http.put<Movimento[]>(
      `/api/tesouraria/carteira/lista-by-comerciante/${pagina}`,
    );
  }

  listaMovimentosPorConta(contaId: number): Promise<Movimento[]> {
    return this.http.put<Movimento[]>(
      `/api/tesouraria/carteira/lista-by-conta/${contaId}`,
    );
  }

  listaTransferencias(maxId: number): Promise<Transferencia[]> {
    return this.http.put<Transferencia[]>(
      `/api/tesouraria/transferencia/lista-by-comerciante/${maxId}`,
    );
  }

  listaTransferenciasPorConta(contaId: number): Promise<Transferencia[]> {
    return this.http.put<Transferencia[]>(
      `/api/tesouraria/transferencia/lista-by-conta/${contaId}`,
    );
  }

  listaCategorias(pagina: number): Promise<CategoriaTesouraria[]> {
    return this.http.put<CategoriaTesouraria[]>(
      `/api/tesouraria/categoria/lista-by-comerciante/${pagina}`,
    );
  }

  criarConta(dados: NovaConta): Promise<TesourariaResponse> {
    return this.http.post<TesourariaResponse>("/api/tesouraria/conta/add", {
      nome: dados.nome,
      tipo: dados.tipo,
      "banco.id": String(dados.bancoId),
      iban: dados.iban,
      numeroConta: dados.numeroConta,
    });
  }

  editarConta(id: number, dados: NovaConta): Promise<TesourariaResponse> {
    return this.http.post<TesourariaResponse>("/api/tesouraria/conta/edit", {
      id: String(id),
      nome: dados.nome,
      tipo: dados.tipo,
      "banco.id": String(dados.bancoId),
      iban: dados.iban,
      numeroConta: dados.numeroConta,
    });
  }

  criarCategorias(
    nomes: string[],
  ): Promise<AddCategoriasTesourariaResponse> {
    const params = new URLSearchParams();
    for (const nome of nomes) params.append("nomes", nome);
    return this.http.post<AddCategoriasTesourariaResponse>(
      "/api/tesouraria/categoria/add",
      params,
    );
  }

  apagarConta(id: number, nome: string): Promise<TesourariaResponse> {
    return this.http.post<TesourariaResponse>("/api/tesouraria/conta/delete", {
      id: String(id),
      nome,
    });
  }

  editarCategoria(id: number, nome: string): Promise<TesourariaResponse> {
    return this.http.post<TesourariaResponse>(
      "/api/tesouraria/categoria/edit",
      { id: String(id), nome },
    );
  }

  apagarCategoria(id: number, nome: string): Promise<TesourariaResponse> {
    return this.http.post<TesourariaResponse>(
      "/api/tesouraria/categoria/delete",
      { id: String(id), nome },
    );
  }

  criarMovimento(
    tipo: "Receita" | "Despesa",
    dados: NovoMovimento,
  ): Promise<TesourariaResponse> {
    const formData = new FormData();
    formData.append("conta.id", String(dados.contaId));
    formData.append("categoria.id", String(dados.categoriaId));
    formData.append("txtValor", Formatter.moeda(dados.valor));
    formData.append("dataOPR", dados.data);
    formData.append("descricao", dados.descricao);
    if (dados.comprovativo) {
      formData.append("anexoComprovativo", dados.comprovativo);
    }
    return this.http.postMultipart<TesourariaResponse>(
      `/api/tesouraria/carteira/add/${tipo}`,
      formData,
    );
  }

  criarTransferencia(dados: NovaTransferencia): Promise<TesourariaResponse> {
    const formData = new FormData();
    formData.append("origem.id", String(dados.origemId));
    formData.append("destino.id", String(dados.destinoId));
    formData.append("categoria.id", String(dados.categoriaId));
    formData.append("txtValor", Formatter.moeda(dados.valor));
    formData.append("descricao", dados.descricao);
    if (dados.comprovativo) {
      formData.append("anexoComprovativo", dados.comprovativo);
    }
    return this.http.postMultipart<TesourariaResponse>(
      "/api/tesouraria/transferencia/add",
      formData,
    );
  }
}
